import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { ArrowBack, Timer, DeliveryDining } from "@mui/icons-material";
import api from "../../api/axios";
import LoadingScreen from "../../components/LoadingScreen";
import SortableHeader from "../../components/SortableHeader";
import { useSortable } from "../../hooks/useSortable";
import { useSearchFilter } from "../../hooks/useSearchFilter";

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms) {
  if (!ms || ms < 0) return "—";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const hrs = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return `${hrs}h ${min}m`;
}

function formatDurationLong(ms) {
  if (!ms || ms < 0) return "—";
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hrs = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hrs}h ${remMin}m ${sec}s`;
}

function getSpeedColor(ms) {
  if (!ms) return "#94a3b8";
  const days = ms / 86400000;
  if (days < 5) return "#16a34a";
  if (days < 10) return "#f59e0b";
  return "#ef4444";
}

function getSpeedLabel(ms) {
  if (!ms) return "—";
  const days = ms / 86400000;
  if (days < 5) return "⚡ Fast";
  if (days < 10) return "⏳ Moderate";
  return "🐢 Slow";
}

const CARD_ACCENTS = {
  totalDelivered: { icon: "#0d9488", bg: "rgba(13,148,136,0.10)", border: "#ccfbf1" },
  avgDeliveryTime: { icon: "#0d9488", bg: "rgba(13,148,136,0.10)", border: "#ccfbf1" },
  fastestDelivery: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "#dcfce7" },
  slowestDelivery: { icon: "#ef4444", bg: "rgba(220,38,38,0.10)", border: "#fee2e2" },
};

export default function AgentAvgDeliveryTime() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allOrders, setAllOrders] = useState([]);

  const { sortKey, sortDir, handleSort, applySort } = useSortable();
  const { searchTerm, setSearchTerm, applyFilter } = useSearchFilter();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agent/orders/accepted");
        setAllOrders(data.orders || []);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const allEntries = useMemo(() => {
    const result = [];
    allOrders.forEach((order) => {
      if (order.deliveryPhase?.status !== "DELIVERED") return;
      if (!order.agent?.assignedAt || !order.deliveryPhase?.deliveredAt) return;
      const diff = new Date(order.deliveryPhase.deliveredAt) - new Date(order.agent.assignedAt);
      if (diff <= 0) return;
      const vendor = order.items?.[0]?.vendorId;
      result.push({
        orderId: order._id,
        orderIdShort: order._id?.substring(0, 8).toUpperCase(),
        total: order.total,
        vendorName: typeof vendor === "object" ? vendor.store?.businessName || "-" : "-",
        assignedAt: order.agent.assignedAt,
        deliveredAt: order.deliveryPhase.deliveredAt,
        deliveryTimeMs: diff,
      });
    });
    return result;
  }, [allOrders]);

  const sortConfig = useMemo(() => ({
    deliveredAt: { getValue: (e) => new Date(e.deliveredAt || 0).getTime() },
    orderId: { getValue: (e) => (e.orderIdShort || "").toLowerCase() },
    vendorName: { getValue: (e) => (e.vendorName || "").toLowerCase() },
    total: { getValue: (e) => Number(e.total) || 0 },
    assignedAt: { getValue: (e) => new Date(e.assignedAt || 0).getTime() },
    deliveryTimeMs: { getValue: (e) => e.deliveryTimeMs },
  }), []);

  const searchConfig = useMemo(() => [
    { getValue: (e) => e.vendorName },
    { getValue: (e) => e.orderIdShort },
    { getValue: (e) => e.total },
    { getValue: (e) => e.deliveredAt && new Date(e.deliveredAt).toLocaleDateString("en-IN") },
  ], []);

  const filteredEntries = useMemo(() => applyFilter(allEntries, searchConfig), [allEntries, searchConfig, applyFilter]);
  const sortedEntries = useMemo(() => applySort(filteredEntries, sortConfig), [filteredEntries, sortConfig, applySort]);

  const totalCount = allOrders.length;

  const stats = useMemo(() => {
    if (allEntries.length === 0) {
      return { count: 0, avg: null, fastest: null, slowest: null };
    }
    const totalMs = allEntries.reduce((sum, e) => sum + e.deliveryTimeMs, 0);
    return {
      count: allEntries.length,
      avg: totalMs / allEntries.length,
      fastest: allEntries[0].deliveryTimeMs,
      slowest: allEntries[allEntries.length - 1].deliveryTimeMs,
    };
  }, [allEntries]);

  const kpiCards = [
    { id: "totalDelivered", title: "Total Delivered", value: stats.count },
    { id: "avgDeliveryTime", title: "Avg Delivery Time", value: stats.avg ? formatDuration(stats.avg) : "—" },
    { id: "fastestDelivery", title: "Fastest Delivery", value: stats.fastest ? formatDuration(stats.fastest) : "—" },
    { id: "slowestDelivery", title: "Slowest Delivery", value: stats.slowest ? formatDuration(stats.slowest) : "—" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>🚚 Avg Delivery Time Details</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Detailed breakdown of your delivery speed from acceptance to completion</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* KPI Cards */}
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, alignItems: "stretch", mb: 3 }}>
          {kpiCards.map((c) => {
            const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.totalDelivered;
            return (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: "12px", bgcolor: "#fff", border: `1.5px solid ${accent.border}`, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", boxSizing: "border-box", minHeight: { xs: 80, sm: 80, md: 90 } }}>
                <Box sx={{ width: { xs: 34, sm: 38, lg: 36 }, height: { xs: 34, sm: 38, lg: 36 }, borderRadius: "10px", bgcolor: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DeliveryDining sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", lg: "0.68rem" }, fontWeight: 500, color: "#64748b", lineHeight: 1.2, mb: 0.4, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</Typography>
                  {loading ? (
                    <Box sx={{ width: "70%", height: 24, bgcolor: "#e2e8f0", borderRadius: 1 }} />
                  ) : (
                    <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {loading ? (
          <LoadingScreen message="Loading delivery data..." />
        ) : (
          <>
            {/* Speed Distribution */}
            {allEntries.length > 0 && stats.avg && (
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1e293b", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <Timer sx={{ fontSize: 18, color: "#0d9488" }} />
                  Delivery Speed Distribution
                </Typography>
                {(() => {
                  const fast = allEntries.filter((e) => e.deliveryTimeMs < 5 * 86400000).length;
                  const moderate = allEntries.filter((e) => e.deliveryTimeMs >= 5 * 86400000 && e.deliveryTimeMs < 10 * 86400000).length;
                  const slow = allEntries.filter((e) => e.deliveryTimeMs >= 10 * 86400000).length;
                  const total = allEntries.length || 1;
                  return (
                    <Box>
                      <Box sx={{ display: "flex", borderRadius: 1.5, overflow: "hidden", height: 28, mb: 1 }}>
                        {fast > 0 && <Box sx={{ width: `${(fast / total) * 100}%`, bgcolor: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{fast}</Typography></Box>}
                        {moderate > 0 && <Box sx={{ width: `${(moderate / total) * 100}%`, bgcolor: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{moderate}</Typography></Box>}
                        {slow > 0 && <Box sx={{ width: `${(slow / total) * 100}%`, bgcolor: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", minWidth: 40 }}><Typography variant="caption" fontWeight={700} sx={{ color: "#fff", fontSize: "0.65rem" }}>{slow}</Typography></Box>}
                      </Box>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#16a34a" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Fast ({'\u003C'}5 days)</Typography></Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#f59e0b" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Moderate (5-10 days)</Typography></Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#ef4444" }} /><Typography variant="caption" sx={{ color: "#64748b" }}>Slow ({'\u003E'}10 days)</Typography></Box>
                      </Box>
                    </Box>
                  );
                })()}
              </Paper>
            )}

            {/* Details Table */}
            <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
                  <DeliveryDining sx={{ fontSize: 18, color: "#0d9488" }} />
                  Delivery Time Details ({allEntries.length} delivered of {totalCount} total orders)
                </Typography>
              </Box>

              <TextField
                size="small"
                placeholder="🔍 Filter deliveries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mx: 2, mt: 1.5, mb: 0, "& input": { fontSize: "0.82rem" } }}
              />

              {allEntries.length === 0 ? (
                <Box sx={{ p: 6, textAlign: "center" }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(13,148,136,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}><DeliveryDining sx={{ fontSize: 32, color: "#0d9488" }} /></Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No delivery data</Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>Complete some deliveries to see your metrics here.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <SortableHeader label="Delivered Date" column="deliveredAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortableHeader label="Order ID" column="orderId" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortableHeader label="Vendor" column="vendorName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortableHeader label="Amount" column="total" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortableHeader label="Accepted" column="assignedAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortableHeader label="Duration" column="deliveryTimeMs" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Speed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedEntries.map((entry, idx) => (
                        <TableRow key={`${entry.orderId}-${idx}`} sx={{ "&:hover": { bgcolor: "#f8fafc" }, cursor: "pointer" }} onClick={() => navigate(`/agent/orders/${entry.orderId}`)}>
                          <TableCell><Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", whiteSpace: "nowrap" }}>{formatDate(entry.deliveredAt)}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1976d2", fontSize: "0.78rem" }}>#{entry.orderIdShort}</Typography></TableCell>
                          <TableCell><Typography variant="body2" sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{entry.vendorName}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{formatCurrency(entry.total)}</Typography></TableCell>
                          <TableCell><Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>{new Date(entry.assignedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={700} sx={{ color: getSpeedColor(entry.deliveryTimeMs), fontSize: "0.78rem" }}>{formatDurationLong(entry.deliveryTimeMs)}</Typography></TableCell>
                          <TableCell><Chip label={getSpeedLabel(entry.deliveryTimeMs)} size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 22, bgcolor: `${getSpeedColor(entry.deliveryTimeMs)}15`, color: getSpeedColor(entry.deliveryTimeMs), border: `1px solid ${getSpeedColor(entry.deliveryTimeMs)}30` }} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}